import { type AxiosResponse } from 'axios'
import api from '../utils/api'
import { type IDistributionSeries } from '../models/DistributionSeries'
import { type DataObject, type IPagination } from '../models/DataObject'

interface ReopenSectionParams {
  slotId: string
  sectionId: string
  endTimestamp: string
  earlyAccessReleaseTimestamp1?: string | null
  earlyAccessReleaseTimestamp2: string | null

}

interface UpdateSectionSurveyDatesParams {
  slotId: string
  sectionId: string
  endTimestamp: string
  /** Published sections only — omitted on Live and Closed, where the start date is locked. */
  startTimestamp?: string
  /** Cross-listed cards only — the instructor's choice of whether siblings move too. */
  applyToCrossListed?: boolean
}

type ExportStudentSurveySubmissionParams = Record<string, any>

export interface UpdateTemplateRevisionParams {
  distributionId: string
}

export interface UpdateTemplateRevisionForSlotParams {
  slotId: string
}

export type DistributionSeriesListItem = DataObject<IDistributionSeries>

export const createDistributionPlan = async (projectId: string, data: Record<string, any>): Promise<AxiosResponse> => {
  return await api.post(`/api/distribution_series/${projectId}/slots/`, data)
}

export const updateProject = async (projectId: string, data: Record<string, any>): Promise<AxiosResponse> => {
  return await api.patch(`/api/distribution_series/${projectId}`, data)
}

export const createProject = async (data: Record<string, any>): Promise<AxiosResponse> => {
  return await api.post('/api/distribution_series/', data)
}

export const deleteProject = async ({ projectId }: { projectId: string }): Promise<AxiosResponse> => {
  return await api.delete(`/api/distribution_series/${projectId}`)
}

export const exportPdf = async (termId: string, exportBy?: string, mergePdf?: boolean): Promise<AxiosResponse> => {
  const params = new URLSearchParams({ term_id: termId })
  if (exportBy) params.set('export_by', exportBy)
  if (mergePdf) params.set('merge_pdf', 'true')
  return await api.get(`/api/users/reports/export/pdf/?${params.toString()}`, {
    responseType: 'blob',
    headers: { Accept: 'application/zip' }
  })
}

export const exportPDFByInstructorId = async (instructorId: string, termId: string, exportBy?: string, mergePdf?: boolean): Promise<AxiosResponse> => {
  const params = new URLSearchParams({ term_id: termId })
  if (exportBy) params.set('export_by', exportBy)
  if (mergePdf) params.set('merge_pdf', 'true')
  return await api.get(`/api/users/${instructorId}/reports/export/pdf/?${params.toString()}`, {
    responseType: 'blob',
    headers: { Accept: 'application/zip' }
  })
}

export const exportCSV = async (termId: string, exportBy?: string): Promise<AxiosResponse> => {
  const params = new URLSearchParams({ term_id: termId })
  if (exportBy) params.set('export_by', exportBy)
  return await api.get(`/api/users/reports/export/csv/?${params.toString()}`, {
    responseType: 'blob',
    headers: { Accept: 'application/zip' }
  })
}

export const exportCSVByInstructorId = async (instructorId: string, termId: string, exportBy?: string): Promise<AxiosResponse> => {
  const params = new URLSearchParams({ term_id: termId })
  if (exportBy) params.set('export_by', exportBy)
  return await api.get(`/api/users/${instructorId}/reports/export/csv/?${params.toString()}`, {
    responseType: 'blob',
    headers: { Accept: 'application/zip' }
  })
}

/**
 * `sandbox` on `GET /distribution_series/` (CR-341).
 *
 * The default is `Exclude`, so sending nothing means "real projects only" — not "everything I may
 * see", which is what it used to mean. An unrecognised value is a 400 rather than a fall back to
 * the default, which is why this is a closed set rather than a free string. `Include` is not a
 * permission: the backend's visibility filter runs ahead of it, so a caller without a Global-level
 * role still sees no rehearsals.
 */
export enum SandboxSeriesFilter {
  /** Projects with at least one non-sandboxed term, plus projects with no terms yet. The default. */
  Exclude = 'exclude',
  /** Projects with at least one sandboxed term. What `show_sandbox_reports_only=true` maps to. */
  Only = 'only',
  /** No sandbox filter of its own. */
  Include = 'include'
}

type GetAllDistributionSeriesParams = Record<string, any>
interface GetAllDistributionSeriesOptions {
  isPublic?: boolean
}
export interface GetAllDistributionSeriesResult {
  data: DistributionSeriesListItem[]
  permissions?: string[]
}
// • Fetches all Distribution Series from the API
// • Uses `/public/` endpoint when `options.isPublic` is true
export const getAllDistributionSeries = async (params?: GetAllDistributionSeriesParams, options?: GetAllDistributionSeriesOptions): Promise<GetAllDistributionSeriesResult> => {
  const baseUrl = '/api/distribution_series/'
  const url = options?.isPublic ? `${baseUrl}public/` : baseUrl
  const response = await api.get(url, {
    headers: {
      Accept: 'application/cdg'
    },
    ...(params ? { params } : {})
  })
  const apiResponse: { data: Array<DataObject<IDistributionSeries>>, permissions: string[] } = response.data
  return { data: apiResponse?.data, permissions: apiResponse?.permissions }
}

export const getDistributionById = async (urlId: string, params?: Record<string, any>): Promise<AxiosResponse> => {
  return await api.get(`/api/distribution_series/${urlId}/slots/`, {
    params,
    headers: {
      Accept: 'application/cdg'
    }
  })
}
export const updateDistributionPlan = async (slotId: string, data: Record<string, any>): Promise<AxiosResponse> => {
  return await api.patch(`/api/slots/${slotId}`, data)
}

export const addSectionToSurveyProject = async (slotId: string, data: Record<string, any>): Promise<AxiosResponse> => {
  return await api.post(`/api/slots/${slotId}/sections/`, data)
}

export const getProjectById = async (urlId: string, params?: Record<string, any>): Promise<AxiosResponse> => {
  return await api.get(`/api/distribution_series/${urlId}`, {
    params
  })
}

export const reopenSurveyProject = async (slotId: string, data: Record<string, any>): Promise<AxiosResponse> => {
  return await api.post(`/api/slots/${slotId}/reopen/`, data)
}

/**
 * The instructor dashboard's card list, one page at a time.
 *
 * `Accept: application/cdg` opts into the paginated envelope; without it the endpoint still
 * returns today's bare array (the backend keeps that path for backwards compatibility, so
 * sending the header is what makes pagination happen at all).
 *
 * CDG nests each row as `data[i].data`. We flatten here on purpose: the card component, the
 * `InstructorDistribution` model and `ReportsPageV2` all read these objects directly, so
 * letting the envelope past this boundary would ripple through ~29 field accesses for no
 * benefit. We do not consume the per-row `actions`/`permissions` blocks — nothing on the card
 * gates on them today. Same shape as `AutoImportManager`'s `response.data.data` unwrap.
 *
 * `pageNumber`/`pageSize` are sent camelCase and reach the backend as `page_number`/`page_size`
 * via the shared client's case-conversion middleware (see `src/utils/api.ts`) — the same
 * mechanism that turns `termId` into `term_id`. Do not hand-write the snake_case forms.
 *
 * Spec: docs/specs/active/2026-08-19-instructor-dashboard-pagination/spec.md (BC-5)
 */
export interface InstructorDistributionsPage {
  data: any[]
  pagination?: IPagination
}

/**
 * Shared by the instructor and admin dashboards, whose paging contracts the backend made
 * byte-identical on purpose so one frontend shape serves both.
 *
 * Defensive on a bare array: if the envelope is ever absent — header stripped by a proxy, or a
 * backend that predates it — the cards still render rather than the dashboard reading as empty.
 */
const toDashboardPage = (body: any): InstructorDistributionsPage => {
  if (Array.isArray(body)) return { data: body }
  return {
    data: (body?.data ?? []).map((row: any) => row?.data ?? row),
    pagination: body?.pagination
  }
}

export const getInstructorDistributionsById = async (instructorId: string, params: Record<string, any>): Promise<InstructorDistributionsPage> => {
  const response = await api.get(`/api/distributions/instructor/${instructorId}`, {
    headers: { Accept: 'application/cdg' },
    params
  })
  return toDashboardPage(response.data)
}

export const goLiveSectionApi = async (params: Record<string, any>): Promise<AxiosResponse> => {
  return await api.post(`/api/distributions/?slot_id=${params.slotId}&section_id=${params.sectionId}`)
}

export const reopenSectionSurvey = async ({ slotId, sectionId, endTimestamp, earlyAccessReleaseTimestamp1, earlyAccessReleaseTimestamp2 }: ReopenSectionParams): Promise<AxiosResponse> => {
  return await api.patch(`/api/slots/${slotId}/sections/${sectionId}/reopen/`, { endTimestamp, earlyAccessReleaseTimestamp1, earlyAccessReleaseTimestamp2 })
}

/**
 * The instructor's own survey-date change on one course section (CR-337).
 *
 * `startTimestamp` is sent **only** for a Published section: the endpoint refuses a changed start
 * on a Live or Closed section, so the caller omits it rather than echoing the current value back.
 * `applyToCrossListed` is sent only from a cross-listed card, where the instructor made a choice.
 *
 * Release timestamps are never sent. The backend derives both from the new end date — one hour
 * after it on Published/Live, one day after it on a Closed reopen.
 */
export const updateSectionSurveyDates = async (
  { slotId, sectionId, endTimestamp, startTimestamp, applyToCrossListed }: UpdateSectionSurveyDatesParams
): Promise<AxiosResponse> => {
  return await api.patch(`/api/slots/${slotId}/sections/${sectionId}/survey-dates`, {
    endTimestamp,
    ...(startTimestamp != null ? { startTimestamp } : {}),
    ...(applyToCrossListed != null ? { applyToCrossListed } : {})
  })
}

export const closeSurveySectionApi = async ({ slotId, sectionId }: { slotId: string, sectionId: string }): Promise<AxiosResponse> => {
  return await api.patch(`/api/slots/${slotId}/sections/${sectionId}/close/`)
}

// Per-section (individual course-section survey) — path-keyed by distribution id.
export const updateTemplateRevision = async ({ distributionId }: UpdateTemplateRevisionParams): Promise<AxiosResponse> => {
  return await api.post(`/api/distributions/update-template/${distributionId}`)
}

// Project-wide (the whole survey project / slot) — keyed by `slot_id` query param.
export const updateTemplateRevisionForSlot = async ({ slotId }: UpdateTemplateRevisionForSlotParams): Promise<AxiosResponse> => {
  return await api.post('/api/distributions/update-template/', null, { params: { slot_id: slotId } })
}

/** Self variant of `getInstructorDistributionsById`. Same envelope, same flatten. */
export const getInstructorDistributions = async (params: Record<string, any>): Promise<InstructorDistributionsPage> => {
  const response = await api.get('/api/distributions/instructor/', {
    headers: { Accept: 'application/cdg' },
    params
  })
  return toDashboardPage(response.data)
}

/**
 * The admin dashboard's card list, one page at a time (CR-296).
 *
 * Contract is byte-identical to the instructor endpoint — the backend built it that way
 * deliberately — so this shares `toDashboardPage` and the same `Accept: application/cdg` opt-in.
 * Note the Accept match is exact string equality on their side: a client that appends
 * `, application/json;q=0.9` silently gets the unpaginated array instead.
 *
 * One thing worth knowing before assuming this makes the page fast. Pagination bounds the number
 * of cards, not the cost of a card: per-card cost is roughly 28 statements plus 4.6 per section
 * beneath that division, so a single high-level division card measured 882 statements over a
 * 192-section subtree and `page_size` cannot reduce it. At one customer, bounding 4,304 cards to
 * 20 is still a large win — but the per-card axis belongs to the backend's division-report cache
 * and rolling results, not to us.
 *
 * Spec: docs/specs/active/2026-08-21-admin-dashboard-pagination/spec.md
 */
export const getAdminDistributions = async (params: Record<string, any>): Promise<InstructorDistributionsPage> => {
  const response = await api.get('/api/distributions/admin/', {
    headers: { Accept: 'application/cdg' },
    params
  })
  return toDashboardPage(response.data)
}

/**
 * The student dashboard's survey list, one page at a time (CR-293).
 *
 * Same envelope handling as the instructor endpoint — see `getInstructorDistributions` — with one
 * addition: `actionable_count`, computed server-side over the whole result set, not the page.
 * Two nav surfaces depend on it (the "Take Surveys" button and the due-surveys badge), and both
 * previously derived it from the full array, which pagination would have broken silently.
 *
 * `actionableCount` is NOT derivable from `totalResults`: the list includes surveys that closed
 * within the last 30 days, which count toward the total and are deliberately excluded from the
 * actionable count. It is also envelope-only — without `Accept: application/cdg` the backend
 * sends neither the count nor a pagination block.
 *
 * Backend contract: `Backend/docs/specs/active/2026-08-21-student-dashboard-pagination/`.
 * Note the backend builds this count from its resubmit *authorization* gate, so it is reopen-aware
 * on tenants where a submitted survey stays reopenable — the count can never disagree with what
 * the backend would actually permit.
 */
export interface StudentDistributionsPage {
  data: any[]
  pagination?: IPagination
  /** Absent when the response was a bare array, i.e. the backend has not shipped the envelope. */
  actionableCount?: number
}

export const getStudentDistributions = async (params: Record<string, any> = {}): Promise<StudentDistributionsPage> => {
  const response = await api.get('/api/distributions/students/', {
    headers: { Accept: 'application/cdg' },
    params
  })
  const body = response.data
  // Bare array: no envelope, so no pagination and no count — but it is the complete list, which
  // is what makes the caller's fallback sound. See useStudentDistributions.
  if (Array.isArray(body)) return { data: body }
  return {
    data: (body?.data ?? []).map((row: any) => row?.data ?? row),
    pagination: body?.pagination,
    actionableCount: body?.actionableCount
  }
}

const getDistributionSeriesBySeriesId = async (seriesId: string): Promise<AxiosResponse> => {
  return await api.get(`/api/distribution_series/${seriesId}`)
}

const getSlotsForDistributionSeriesBySeriesId = async (seriesId: string): Promise<AxiosResponse> => {
  return await api.get(`/api/distribution_series/${seriesId}/slots/`)
}

const exportSectionsCSV = async (params: { slotId: string, search?: string, sort?: string, pageSize?: number }): Promise<AxiosResponse> => {
  const { slotId, ...queryParams } = params
  return await api.get(`/api/slots/${slotId}/sections/export/`, { params: queryParams })
}

export const resetCourseSections = async (slotId: string): Promise<AxiosResponse> => {
  return await api.delete(`/api/slots/${slotId}/sections/reset/`)
}

export const getSlot = async (slotId: string): Promise<AxiosResponse> => {
  return await api.get(`/api/slots/${slotId}`, {
    headers: {
      Accept: 'application/cdg'
    }
  })
}

const deleteSlotBySlotId = async (slotId: string): Promise<AxiosResponse> => {
  return await api.delete(`/api/slots/${slotId}`)
}

const exportEnrollmentsCSV = async (params?: Record<string, any>): Promise<AxiosResponse> => {
  return await api.get('/api/enrollments/export/', { params })
}

const exportInstructorsAssignmentsCSV = async (params?: Record<string, any>): Promise<AxiosResponse> => {
  return await api.get('/api/users/roles/export/', { params })
}

const exportStudentSurveySubmission = async (
  slotId: string,
  params?: ExportStudentSurveySubmissionParams
): Promise<AxiosResponse> => {
  return await api.get(`/api/slots/${slotId}/survey_submissions/export/`, { params })
}

export {
  getDistributionSeriesBySeriesId,
  getSlotsForDistributionSeriesBySeriesId,
  deleteSlotBySlotId
}

export default {
  exportPdf,
  exportPDFByInstructorId,
  exportCSV,
  exportCSVByInstructorId,
  getAllDistributionSeries,
  getDistributionSeriesBySeriesId,
  getSlotsForDistributionSeriesBySeriesId,
  getInstructorDistributionsById,
  getInstructorDistributions,
  reopenSurveyProject,
  goLiveSectionApi,
  getAdminDistributions,
  getStudentDistributions,
  exportSectionsCSV,
  resetCourseSections,
  getDistributionById,
  deleteProject,
  getProjectById,
  updateProject,
  createProject,
  deleteSlotBySlotId,
  updateDistributionPlan,
  addSectionToSurveyProject,
  getSlot,
  createDistributionPlan,
  reopenSectionSurvey,
  updateSectionSurveyDates,
  closeSurveySectionApi,
  updateTemplateRevision,
  updateTemplateRevisionForSlot,
  exportEnrollmentsCSV,
  exportInstructorsAssignmentsCSV,
  exportStudentSurveySubmission
}
